import React, { useMemo, useState } from 'react'
import { Box, Card, Tag, Typography } from '@octanner/prism-core'
import { StyledCardContent } from '../../common/components/Styled'
import { CelebrationLevelGroupConfig } from '../../common/models/CelebrationLevelGroupConfig'
import { rootFactories } from '../../models/CelebrationCard'
import CelebrationTagContainer from './CelebrationTagContainer'
import ConfigDetailContainer from './ConfigDetailContainer'
import MenuButton from '../MenuButton'
import { genYearsText } from './utils'
import CelebrationGroupActionScheduleContainer from '../CelebrationGroupSchedule/CelebrationGroupActionScheduleContainer'
import ConfirmationModal from '../ConfirmationModal'
import useCelebrationLevelGroupStatus from '../../hooks/useCelebrationLevelGroupStatus'
import { tannerGray } from '@octanner/prism-core/ThemeProvider/colors'

interface Props {
  celebrationGroup: CelebrationLevelGroupConfig
  onEditClick: (groupId?: string) => void
  programId: string
}

export default function CelebrationCard({
  celebrationGroup,
  onEditClick,
  programId,
}: Props): JSX.Element {
  const details = rootFactories.map((factory) => factory(celebrationGroup))
  const yearsText = useMemo(
    () =>
      genYearsText(celebrationGroup.celebrationLevelConfigs.map((c) => c.rank)),
    [celebrationGroup.celebrationLevelConfigs]
  )
  const [resumegroupActionState, setResumegroupActionState] = useState<{
    isOpen: boolean
    action: 'suspend' | 'resume'
  }>({
    isOpen: false,
    action: 'suspend',
  })

  const resetResumegroupActionState = () => {
    setResumegroupActionState((prev) => ({
      ...prev,
      isOpen: false,
    }))
  }

  const openResumegroupModal = (action: 'suspend' | 'resume') => {
    setResumegroupActionState({
      isOpen: true,
      action,
    })
  }

  const {
    updateSuspendGroupAction: resumeGroupAction,
    loading: suspendStatusLoading,
  } = useCelebrationLevelGroupStatus()

  const handleResumeGroup = async () => {
    await resumeGroupAction({
      input: {
        config: {
          suspended: resumegroupActionState.action === 'suspend',
          id: celebrationGroup.id,
        },
        programId: programId,
      },
      onSuccess: () => {
        resetResumegroupActionState()
      },
      onError: () => {
        resetResumegroupActionState()
      },
    })
  }
  const isSuspended = celebrationGroup.suspended

  return (
    <Card
      sx={
        isSuspended && {
          boxShadow: 'none',
          borderRadius: '3px',
          border: `1px solid ${tannerGray[200]}`,
          background: `${tannerGray[25]}`,
        }
      }
    >
      <StyledCardContent>
        <section data-testid={`celebration-group:${celebrationGroup.id}`}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h5" data-testid="name">
              {celebrationGroup.name}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 4,
                alignItems: 'center',
              }}
            >
              {isSuspended && <Tag variant="warning">Suspended</Tag>}
              <MenuButton
                isSuspended={isSuspended}
                onEditClick={() => onEditClick(celebrationGroup.id)}
                onClickResumeGroup={() => openResumegroupModal('resume')}
                onClickSuspendGroup={() => openResumegroupModal('suspend')}
              />
            </Box>
          </Box>

          <CelebrationTagContainer
            details={details.filter((d) => d?.tag === true)}
            milestone={celebrationGroup.milestone}
          />
        </section>
        <section>
          <Typography>Anniversary Years:</Typography>
          <Typography data-testid="anniversary-years">{yearsText}</Typography>
        </section>
        <ConfigDetailContainer details={details} />
        <CelebrationGroupActionScheduleContainer
          showToggleScheduleBtn
          celebrationLevelGroupUuid={celebrationGroup.id}
          programId={programId}
        />
      </StyledCardContent>
      <ConfirmationModal
        open={resumegroupActionState.isOpen}
        titleText={
          resumegroupActionState.action === 'resume'
            ? 'Resume this group?'
            : 'Suspend this group?'
        }
        dataTestId="resumeGroupActionConfirmation"
        messageText={
          resumegroupActionState.action === 'resume'
            ? 'This will enable the creation of associated celebrations to resume. Would you like to continue?'
            : 'This will prohibit the creation of further celebrations. However, any active celebrations will not be affected. Would you like to continue?'
        }
        primaryButtonText={
          resumegroupActionState.action === 'resume'
            ? 'Resume group'
            : 'Suspend group'
        }
        onModalClose={resetResumegroupActionState}
        onModalAction={handleResumeGroup}
        loading={suspendStatusLoading}
      />
    </Card>
  )
}
